// Shared formatting/localization helpers. These were copy-pasted (with subtly
// different fallback orders) across Menu, Catering, News, Branches, etc.

// Pick a localized DB field, e.g. getLocalizedField(item, 'name', 'id').
// Falls back through the requested lang -> Indonesian -> English -> ''.
export function getLocalizedField(obj, field, lang = 'id') {
  if (!obj) return '';
  const code = ['id', 'en', 'ar', 'ms'].includes(lang) ? lang : 'en';
  return obj[`${field}_${code}`] || obj[`${field}_id`] || obj[`${field}_en`] || '';
}

// Has a job posting's application deadline passed?
//
// apply_deadline is a plain calendar date (YYYY-MM-DD) and the deadline day
// itself still counts as open — "Sampai 30 September" includes the 30th — so
// the posting closes at the end of that day. The comparison is done on date
// strings rather than Date objects on purpose: `new Date('2026-09-30')` parses
// as UTC midnight, which in WIB (UTC+7) would shut a posting seven hours early.
//
// A posting with no deadline is open indefinitely; that is the "unlimited"
// case HR picks in the admin panel, stored as null.
export function isDeadlinePassed(deadline) {
  if (!deadline) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return String(deadline).slice(0, 10) < today;
}

const PRICE_FORMATTERS = {
  IDR: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }),
  SGD: new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }),
  MYR: new Intl.NumberFormat('ms-MY', { style: 'currency', currency: 'MYR' }),
  SAR: new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR' }),
  AUD: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }),
  USD: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
};

// Format a price in the given currency. Returns null for empty/zero so callers
// can choose to hide the price entirely.
export function formatPrice(price, currency = 'IDR') {
  if (price === null || price === undefined || price === '') return null;
  const n = Number(price);
  if (Number.isNaN(n)) return null;
  return (PRICE_FORMATTERS[currency] || PRICE_FORMATTERS.IDR).format(n);
}

// Great-circle distance between two lat/lng points, in kilometers.
export function calculateDistance(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v === null || v === undefined)) return null;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
